import { useMemo } from 'react';
import Introduction from '@/pages/CompanyReport/components/Introduction';
import RecentSectionUpdates from '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates';
import CriteriaChecks from '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks';
import KeyInformation from '@/pages/CompanyReport/components/Introduction/components/KeyInformation';

import { useCompany } from '@/hooks/useCompany';
import type { StatementChecks } from '@/pages/CompanyReport/components/Introduction/utils';

import { getFormattedKeyInfo, getPastTerms } from './utils';

export interface PastHeaderProps {
  statementChecks: StatementChecks[];
  canonicalUrl: string;
  industryName: string;
}

export const PastHeader = ({
  statementChecks,
  canonicalUrl,
  industryName,
}: PastHeaderProps) => {
  const { data } = useCompany(canonicalUrl);

  const pastData = data?.data.analysis?.data.extended.data.analysis.past;
  const futureData = data?.data.analysis?.data.extended.data.analysis.future;
  const industryAveragesData =
    data?.data.analysis?.data.extended.data.industry_averages;

  const {
    earningsGrowthRate,
    epsGrowthRate,
    industryGrowthRate,
    revenueGrowthRate,
    returnOnEquity,
    netMargin,
    earningUpdateAt,
    earningUpdateAtLabel,
  } = getFormattedKeyInfo({ pastData, industryAveragesData, futureData });

  const criteriaChecks = useMemo(
    () => <CriteriaChecks scoreName="Past" checks={statementChecks} />,
    [statementChecks]
  );

  const terms = getPastTerms(industryName);

  const keyInformation = useMemo(
    () => (
      <KeyInformation
        direction="row"
        keyMetrics={[
          {
            title: 'Earnings growth rate',
            value: earningsGrowthRate,
            tooltip: terms.earningsGrowthRate,
          },
          {
            title: 'EPS growth rate',
            value: epsGrowthRate,
            tooltip: terms.epsGrowthRate,
          },
        ]}
        metrics={[
          {
            title: `${industryName} Industry Growth`,
            value: industryGrowthRate,
            tooltip: terms.industryGrowthRate,
          },
          {
            title: 'Revenue growth rate',
            value: revenueGrowthRate,
            tooltip: terms.revenueGrowthRate,
          },
          {
            title: 'Return on equity',
            value: returnOnEquity,
            tooltip: terms.returnOnEquity,
          },
          {
            title: 'Net Margin',
            value: netMargin,
            tooltip: terms.netMargin,
          },
          {
            title: earningUpdateAtLabel,
            value: earningUpdateAt,
          },
        ]}
      />
    ),
    [
      earningUpdateAt,
      earningsGrowthRate,
      epsGrowthRate,
      industryGrowthRate,
      netMargin,
      returnOnEquity,
      revenueGrowthRate,
      earningUpdateAtLabel,
      terms.earningsGrowthRate,
      terms.epsGrowthRate,
      terms.industryGrowthRate,
      terms.netMargin,
      terms.returnOnEquity,
      terms.revenueGrowthRate,
      industryName,
    ]
  );

  const recentSectionUpdates = useMemo(
    () => (
      <RecentSectionUpdates
        lookup={canonicalUrl}
        title="Recent past performance updates"
        filterByEventCategory="past"
      />
    ),
    [canonicalUrl]
  );

  return (
    <Introduction
      scoreName="Past"
      criteriaChecks={criteriaChecks}
      keyInformation={keyInformation}
      recentSectionUpdates={recentSectionUpdates}
    />
  );
};
